import React, { useState, useEffect } from "react";
import { FlagIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

// ─── SEO Component ──────────────────────────────────────────────────────────────
function SEOHead() {
  useEffect(() => {
    // Page Title
    document.title = "Campaigns - DonatePE";
    
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    
    // Meta Description
    setMeta("description", "Browse all active fundraising campaigns on DonatePE. Discover medical, education, emergency, and community causes that need your support. Donate to make a difference today.");
    
    // Robots
    setMeta("robots", "index, follow");
    
    // Charset
    let charset = document.querySelector('meta[charset="UTF-8"]');
    if (!charset) {
      charset = document.createElement("meta");
      charset.setAttribute("charset", "UTF-8");
      document.head.insertBefore(charset, document.head.firstChild);
    }
    
    // Viewport
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement("meta");
      viewport.setAttribute("name", "viewport");
      viewport.setAttribute("content", "width=device-width, initial-scale=1.0");
      document.head.appendChild(viewport);
    }
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://donatepe.org/campaigns");
    
    // Open Graph Tags
    setMeta("og:title", "Campaigns - DonatePE", true);
    setMeta("og:description", "Browse all active fundraising campaigns on DonatePE. Discover medical, education, emergency, and community causes that need your support.", true);
    setMeta("og:url", "https://donatepe.org/campaigns", true);
    setMeta("og:type", "website", true);
    setMeta("og:image", "https://donatepe.org/logo.png", true);
    
    // Twitter Card Tags
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", "Campaigns - DonatePE");
    setMeta("twitter:description", "Browse all active fundraising campaigns on DonatePE. Discover causes that need your support.");
    setMeta("twitter:image", "https://donatepe.org/logo.png");
  }, []);
  
  return null;
}

const AllCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const API_BASE_URL = "https://api.donatepe.org";
  const defaultCampaignImage = "/assets/temp_image.png";

  // Function to check if URL is valid (not localhost and not blob)
  const isValidImageUrl = (url) => {
    if (!url) return false;
    if (url.includes('localhost') || url.includes('127.0.0.1')) return false;
    if (url.startsWith('blob:')) return false;
    if (url.includes('blob:http')) return false;
    return true;
  };

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const response = await fetch("https://api.donatepe.org/public/fundraisers");
        const data = await response.json();

        if (data.success && data.fundraisers) {
          // Transform all campaigns (no filtering)
          const transformedCampaigns = data.fundraisers.map((fundraiser) => {
            const progress =
              fundraiser.goal_amount > 0
                ? Math.min(
                    100,
                    Math.round((fundraiser.collected_amount / fundraiser.goal_amount) * 100)
                  )
                : 0;

            const getCategory = () => {
              const title = fundraiser?.title?.toLowerCase() || "";
              if (title.includes("medical") || title.includes("cure") || title.includes("cancer") || title.includes("treatment"))
                return "Medical";
              if (title.includes("education") || title.includes("school") || title.includes("college") || title.includes("student"))
                return "Education";
              if (title.includes("animal") || title.includes("pet") || title.includes("dog") || title.includes("cat"))
                return "Animals";
              if (title.includes("emergency") || title.includes("relief") || title.includes("flood") || title.includes("disaster"))
                return "Emergency";
              return "Community";
            };

            let imageUrl = defaultCampaignImage;
            if (fundraiser.cover_image && isValidImageUrl(fundraiser.cover_image)) {
              if (fundraiser.cover_image.startsWith("/uploads/")) {
                imageUrl = `${API_BASE_URL}${fundraiser.cover_image}`;
              } else if (fundraiser.cover_image.startsWith("http")) {
                imageUrl = fundraiser.cover_image;
              }
            }

            const slug = fundraiser.slug || `campaign-${fundraiser.id}`;

            let description = "Help us make a difference";
            if (slug) {
              try {
                const generatedDesc = slug.replace(/-/g, " ").replace(/\d+/g, "").trim();
                if (generatedDesc) {
                  description = generatedDesc.charAt(0).toUpperCase() + generatedDesc.slice(1);
                }
              } catch (e) {}
            }

            const category = getCategory();

            const getCategoryIcon = () => {
              if (category === "Medical") return "🏥";
              if (category === "Education") return "📚";
              if (category === "Animals") return "🐾";
              if (category === "Emergency") return "⚡";
              return "🤝";
            };

            const getCategoryColor = () => {
              if (category === "Medical") return "from-rose-500 to-red-500";
              if (category === "Education") return "from-blue-500 to-indigo-500";
              if (category === "Animals") return "from-teal-500 to-emerald-500";
              if (category === "Emergency") return "from-amber-500 to-orange-500";
              return "from-purple-500 to-pink-500";
            };

            return {
              id: fundraiser.id,
              title: fundraiser.title || "Help us make a difference",
              description: description,
              category: category,
              categoryIcon: getCategoryIcon(),
              categoryColor: getCategoryColor(),
              raised: fundraiser.collected_amount || 0,
              goal: fundraiser.goal_amount || 0,
              imageUrl: imageUrl,
              isVerified: fundraiser.collected_amount > 0,
              progress: progress,
              slug: slug,
              creatorName: fundraiser.creator_name || "Anonymous",
              createdAt: fundraiser.created_at,
              status: fundraiser.status,
            };
          });

          setCampaigns(transformedCampaigns);
        } else {
          setError("Failed to load campaigns");
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        setError("Something went wrong. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  const handleCardClick = (campaignSlug) => {
    navigate(`/campaign/${campaignSlug}`);
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount}`;
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return "Recently";
    
    const campaignDate = new Date(dateString);
    const today = new Date();
    
    const campaignDay = new Date(campaignDate.getFullYear(), campaignDate.getMonth(), campaignDate.getDate());
    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    const diffTime = todayDay - campaignDay;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  if (loading) {
    return (
      <section className="relative bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-100 rounded-3xl h-[400px] animate-pulse">
                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 rounded-3xl"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="relative bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 max-w-md mx-auto">
            <h3 className="text-xl font-bold text-red-800 mb-2">Oops! Something went wrong</h3>
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <SEOHead />
      {/* Breadcrumb Schema JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://donatepe.org"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Campaigns",
              "item": "https://donatepe.org/campaigns"
            }
          ]
        })}
      </script>
      <section className="relative bg-white py-16 overflow-hidden">
        {/* Background Decor */}
        <div className="absolute inset-0">
          <div className="absolute top-20 -left-20 w-[600px] h-[600px] bg-gradient-to-br from-teal-100/30 to-emerald-100/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 -right-20 w-[600px] h-[600px] bg-gradient-to-br from-blue-100/30 to-indigo-100/30 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight mb-4">
              All{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">
                Campaigns
              </span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover all the causes that need your support. Every contribution makes a difference.
            </p>
          </div>

          {/* Campaigns Grid */}
          {campaigns.length === 0 ? (
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
                <span className="text-3xl">✨</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No campaigns found</h3>
              <p className="text-gray-500">Check back soon for new stories</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {campaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  className="cursor-pointer transform transition-all duration-300 hover:-translate-y-2"
                  onClick={() => handleCardClick(campaign.slug)}
                >
                  <div className="bg-white rounded-3xl overflow-hidden shadow-xl h-[420px] flex flex-col border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
                    {/* Image Section */}
                    <div className="relative h-48 overflow-hidden flex-shrink-0">
                      <img
                        src={campaign.imageUrl}
                        alt={campaign.title}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                        onError={(e) => {
                          e.target.src = defaultCampaignImage;
                        }}
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/30 to-transparent"></div>

                      {/* Category Badge */}
                      <div className="absolute top-3 left-3">
                        <div className="flex items-center gap-1.5 px-2 py-1 bg-white/95 backdrop-blur-sm rounded-full text-[10px] font-medium text-gray-700 shadow-xl">
                          <span>{campaign.categoryIcon}</span>
                          <span>{campaign.category}</span>
                        </div>
                      </div>

                      {/* Verified Badge */}
                      <div className="absolute top-3 right-3">
                        {campaign.isVerified && (
                          <div className="flex items-center gap-1.5 px-2 py-1 bg-white/95 backdrop-blur-sm rounded-full shadow-xl">
                            <FlagIcon className="w-3 h-3 text-emerald-600" />
                            <span className="text-[8px] font-bold text-emerald-700">
                              VERIFIED
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Status Badge for Inactive Campaigns */}
                      {campaign.status === "inactive" && (
                        <div className="absolute bottom-3 right-3">
                          <div className="px-2 py-1 bg-red-500/90 backdrop-blur-sm rounded-full shadow-xl">
                            <span className="text-[8px] font-bold text-white">INACTIVE</span>
                          </div>
                        </div>
                      )}

                      {/* Creator Info */}
                      <div className="absolute bottom-3 left-3 flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white text-[10px] font-bold shadow-2xl border-2 border-white/80">
                          {campaign.creatorName[0]?.toUpperCase() || "U"}
                        </div>
                        <div className="text-white">
                          <p className="text-[10px] font-bold drop-shadow-lg">
                            {campaign.creatorName}
                          </p>
                          <p className="text-[7px] text-white/90">
                            {getTimeAgo(campaign.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-4 flex flex-col flex-grow bg-white">
                      <h3 className="text-base font-bold text-gray-900 mb-1 line-clamp-2">
                        {campaign.title}
                      </h3>
                      <p className="text-xs text-gray-600 mb-3 line-clamp-2 flex-grow">
                        {campaign.description}
                      </p>

                      {/* Progress Section */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-end">
                          <div>
                            <span className="text-lg font-bold text-gray-900">
                              {formatCurrency(campaign.raised)}
                            </span>
                            <span className="text-[10px] text-gray-500 ml-1">
                              of {formatCurrency(campaign.goal)}
                            </span>
                          </div>
                          <span className="text-sm font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-lg">
                            {campaign.progress}%
                          </span>
                        </div>

                        <div className="relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="absolute top-0 left-0 h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${campaign.progress}%`,
                              background: `linear-gradient(90deg, ${
                                campaign.categoryColor.includes("rose")
                                  ? "#f43f5e"
                                  : campaign.categoryColor.includes("blue")
                                  ? "#3b82f6"
                                  : campaign.categoryColor.includes("teal")
                                  ? "#14b8a6"
                                  : campaign.categoryColor.includes("amber")
                                  ? "#f59e0b"
                                  : "#8b5cf6"
                              }, ${
                                campaign.categoryColor.includes("rose")
                                  ? "#ef4444"
                                  : campaign.categoryColor.includes("blue")
                                  ? "#6366f1"
                                  : campaign.categoryColor.includes("teal")
                                  ? "#10b981"
                                  : campaign.categoryColor.includes("amber")
                                  ? "#f97316"
                                  : "#d946ef"
                              })`,
                            }}
                          />
                        </div>

                        {/* Almost there badge */}
                        {campaign.progress > 75 && (
                          <div className="flex items-center justify-end">
                            <div className="flex items-center gap-1 text-[8px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                              <SparklesIcon className="w-2.5 h-2.5" />
                              <span>Almost there!</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Donate Button */}
                      <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCardClick(campaign.slug);
                          }}
                          disabled={campaign.status === "inactive"}
                          className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                            campaign.status === "inactive"
                              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                              : "bg-gradient-to-r from-teal-600 to-emerald-600 text-white hover:shadow-lg hover:scale-105"
                          }`}
                        >
                          {campaign.status === "inactive" ? "Not Available" : "Donate Now"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Results Count */}
          {campaigns.length > 0 && (
            <div className="mt-12 text-center">
              <p className="text-sm text-gray-500">
                Showing <span className="font-semibold text-teal-600">{campaigns.length}</span> campaigns
              </p>
            </div>
          )}
        </div>

        <style jsx>{`
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in {
            animation: fade-in 0.5s ease-out;
          }
        `}</style>
      </section>
    </>
  );
};

export default AllCampaigns;